"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RiArrowGoBackLine, RiCloseLine, RiReceiptLine } from "react-icons/ri";
import { ProgressBarLink } from "@/components/progress-bar";
import Badge from "@/components/ui/badge";
import { getLotHistoryDescription, getLotShipmentDescription } from "@/components/ui/lot-number-details";
import { DynamicSelectItem, SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui/select";
import { useLocations } from "@/hooks/use-locations";
import { cx } from "@/lib/tailwind-utils";
import { type LotNumberHistory, getLotNumbers } from "@/api/provider/scribe/notes";
import { type ShipmentLot, medicationsQueryOptions } from "@/query/medications/medications";
export type LotSelection = {
  type: "lot";
  value: string;
} | {
  type: "shipment";
  id: string;
  lotNumber: string;
};
const SHIPMENT_PREFIX = "shipment::";

// Stable reference so the `lotNumbers` default doesn't change identity per render.
const EMPTY_LOT_NUMBERS: LotNumberHistory[] = [];
const EMPTY_SHIPMENT_LOTS: ShipmentLot[] = [];
const EMPTY_CATEGORIES: string[] = [];
interface LotNumberSelectFieldProps {
  value: LotSelection | null;
  onValueChange: (value: LotSelection | null) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  shipments?: ShipmentLot[];
  isLoadingShipments?: boolean;
  medicationId?: string | null;
  medicationName?: string | null;
  locationId?: string | null;
}

/**
 * Controlled lot selector for callsites that manage lot state directly
 * (e.g. the face-chart annotator).
 */
export function LotNumberSelectField(t0) {
  const $ = _c(113);
  const {
    value: fieldValue,
    onValueChange,
    placeholder: t1,
    disabled: t2,
    hasError: t3,
    className: t4,
    shipments,
    isLoadingShipments: t5,
    medicationId,
    medicationName,
    locationId
  } = t0;
  const placeholder = t1 === undefined ? "Select lot number" : t1;
  const disabled = t2 === undefined ? false : t2;
  const hasError = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  const isLoadingShipments = t5 === undefined ? false : t5;
  const inventoryMode = shipments != null;
  const hasShipments = inventoryMode && shipments.length > 0;
  const {
    allLocationNameById
  } = useLocations();
  const locationName = locationId ? allLocationNameById[locationId] : undefined;
  const [isWidened, setIsWidened] = useState(false);
  const [selectionNotice, setSelectionNotice] = useState(null);
  const [widenCategory, setWidenCategory] = useState(null);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsWidened(false);
      setWidenCategory(null);
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  let t7;
  if ($[1] !== medicationId) {
    t7 = [medicationId];
    $[1] = medicationId;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  useEffect(t6, t7);
  const historyEnabled = !isLoadingShipments && !!medicationId && !isWidened;
  const t8 = medicationId ?? null;
  const t9 = locationId ?? null;
  let t10;
  if ($[3] !== t8 || $[4] !== t9) {
    t10 = ["lot-numbers", t8, t9];
    $[3] = t8;
    $[4] = t9;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const t11 = t10 as const;
  let t12;
  if ($[6] !== locationId || $[7] !== medicationId) {
    t12 = async () => {
      const history = await getLotNumbers({
        medicationId: medicationId ?? undefined,
        locationId: locationId ?? undefined
      });
      return Array.from(new Map((history ?? []).map(_temp)).values());
    };
    $[6] = locationId;
    $[7] = medicationId;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  let t13;
  if ($[9] !== historyEnabled || $[10] !== t11 || $[11] !== t12) {
    t13 = {
      queryKey: t11,
      queryFn: t12,
      enabled: historyEnabled,
      staleTime: 0,
      refetchOnMount: "always"
    };
    $[9] = historyEnabled;
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const {
    data: t14,
    isPending
  } = useQuery(t13);
  const lotNumbers = t14 === undefined ? EMPTY_LOT_NUMBERS : t14;
  const isLoading = historyEnabled && isPending;
  let t15;
  if ($[13] !== locationId || $[14] !== widenCategory) {
    t15 = medicationsQueryOptions.availableLotsAcrossMedications(widenCategory ?? undefined, locationId ?? undefined);
    $[13] = locationId;
    $[14] = widenCategory;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== isWidened || $[17] !== t15) {
    t16 = {
      ...t15,
      enabled: isWidened,
      select: _temp2
    };
    $[16] = isWidened;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  const {
    data: t17,
    isPending: isWidenPending
  } = useQuery(t16);
  const widenedLots = t17 === undefined ? EMPTY_SHIPMENT_LOTS : t17;
  const isWidenLoading = isWidened && isWidenPending;
  let t18;
  if ($[19] !== locationId) {
    t18 = medicationsQueryOptions.lotCategories(locationId ?? undefined);
    $[19] = locationId;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== isWidened || $[22] !== t18) {
    t19 = {
      ...t18,
      enabled: isWidened
    };
    $[21] = isWidened;
    $[22] = t18;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const {
    data: lotCategories
  } = useQuery(t19);
  let t20;
  if ($[24] !== allLocationNameById) {
    t20 = shipment => getLotShipmentDescription(shipment, allLocationNameById);
    $[24] = allLocationNameById;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const getShipmentDescription = t20;
  const loading = isLoading || isLoadingShipments || isWidenLoading;
  let t21;
  if ($[26] !== isWidened || $[27] !== onValueChange || $[28] !== shipments) {
    t21 = newValue => {
      if (!newValue) {
        onValueChange(null);
        return;
      }
      if (newValue.startsWith(SHIPMENT_PREFIX)) {
        const shipmentId = newValue.slice(SHIPMENT_PREFIX.length);
        const shipment_0 = shipments?.find(s => s.id === shipmentId);
        if (shipment_0) {
          onValueChange({
            type: "shipment",
            id: shipment_0.id,
            lotNumber: shipment_0.lotNumber || ""
          });
        }
      } else {
        const matches = !isWidened ? shipments?.filter(lot => lot.lotNumber?.trim().toLowerCase() === newValue.trim().toLowerCase()) ?? [] : [];
        if (matches.length > 1) {
          setSelectionNotice("Multiple inventory entries have this lot number. Use the supplier, location, and expiry to select the correct lot above.");
          return;
        }
        if (matches[0]) {
          onValueChange({
            type: "shipment",
            id: matches[0].id,
            lotNumber: matches[0].lotNumber ?? ""
          });
          return;
        }
        onValueChange({
          type: "lot",
          value: newValue
        });
      }
    };
    $[26] = isWidened;
    $[27] = onValueChange;
    $[28] = shipments;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  const handleValueChange = t21;
  let t22;
  if ($[30] !== onValueChange) {
    t22 = () => {
      onValueChange(null);
    };
    $[30] = onValueChange;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const handleClear = t22;
  const selectedLotValue = fieldValue?.type === "lot" ? fieldValue.value : null;
  const selectedLotMatchesShipment = hasShipments && selectedLotValue != null && shipments.some(s_0 => s_0.lotNumber?.trim().toLowerCase() === selectedLotValue.trim().toLowerCase());
  const selectValue = fieldValue?.type === "shipment" ? `${SHIPMENT_PREFIX}${fieldValue.id}` : selectedLotValue || "";
  const selectedLotInHistory = selectedLotValue != null && lotNumbers.some(entry_0 => entry_0.lotNumber.trim().toLowerCase() === selectedLotValue.trim().toLowerCase());
  const pinnedFreeformLot = inventoryMode && selectedLotValue && !selectedLotMatchesShipment && (hasShipments || !loading && !selectedLotInHistory) ? selectedLotValue : null;
  let t23;
  if ($[32] !== lotNumbers || $[33] !== medicationName || $[34] !== shipments) {
    t23 = medicationName || shipments?.find(_temp3)?.medicationName || lotNumbers.find(_temp4)?.medicationName || null;
    $[32] = lotNumbers;
    $[33] = medicationName;
    $[34] = shipments;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  const productName = t23;
  const scopedListIsEmpty = !isWidened && !loading && !hasShipments && lotNumbers.length === 0;
  let t24;
  if ($[36] !== className) {
    t24 = cx("relative", className);
    $[36] = className;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== fieldValue || $[39] !== shipments) {
    t25 = val => {
      if (val.startsWith(SHIPMENT_PREFIX)) {
        const shipmentId_0 = val.slice(SHIPMENT_PREFIX.length);
        const shipment_2 = shipments?.find(s_1 => s_1.id === shipmentId_0);
        return shipment_2?.lotNumber || (fieldValue?.type === "shipment" && fieldValue.id === shipmentId_0 ? fieldValue.lotNumber || "Unavailable lot" : "Unavailable lot");
      }
      return val;
    };
    $[38] = fieldValue;
    $[39] = shipments;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== placeholder || $[42] !== t25) {
    t26 = <SearchSelectValue placeholder={placeholder} formatValue={t25} />;
    $[41] = placeholder;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== disabled || $[45] !== hasError || $[46] !== t26) {
    t27 = <SearchSelectTrigger hasError={hasError} disabled={disabled}>{t26}</SearchSelectTrigger>;
    $[44] = disabled;
    $[45] = hasError;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const t28 = loading ? "Checking inventory..." : "No lot numbers found.";
  let t29;
  if ($[48] !== pinnedFreeformLot) {
    t29 = pinnedFreeformLot && <SearchSelectItem value={pinnedFreeformLot}>{lotRowLabel({
        lotNumber: pinnedFreeformLot
      })}</SearchSelectItem>;
    $[48] = pinnedFreeformLot;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== getShipmentDescription || $[51] !== hasShipments || $[52] !== isWidened || $[53] !== productName || $[54] !== shipments) {
    t30 = !isWidened && hasShipments && ["available", "recorded", "depleted"].map(category => {
      const group = shipments.filter(shipment_3 => {
        const ownOrCharged = shipment_3.isDoseComponentShipment || shipment_3.chargedTodayQuantity != null;
        const kind = ownOrCharged ? "recorded" : (shipment_3.providerAvailableQuantity ?? shipment_3.remainingQuantity) <= 0 ? "depleted" : "available";
        return kind === category;
      });
      if (!group.length) {
        return null;
      }
      return <div key={category}>{group.map(shipment_4 => <SearchSelectItem key={shipment_4.id} value={`${SHIPMENT_PREFIX}${shipment_4.id}`} description={getShipmentDescription(shipment_4)}>{lotRowLabel({
            lotNumber: shipment_4.lotNumber,
            productName: shipment_4.medicationName ?? productName,
            chargedToday: shipment_4.chargedTodayQuantity != null
          })}</SearchSelectItem>)}</div>;
    });
    $[50] = getShipmentDescription;
    $[51] = hasShipments;
    $[52] = isWidened;
    $[53] = productName;
    $[54] = shipments;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== hasShipments || $[57] !== inventoryMode || $[58] !== isWidened || $[59] !== loading || $[60] !== locationName || $[61] !== lotNumbers) {
    t31 = inventoryMode && !isWidened && !hasShipments && !loading && <div className="border-t border-border px-3 py-2 text-body text-foreground-secondary"><span><span>{locationName ? `No lot-numbered stock for this medication at ${locationName}.` : "No lot-numbered stock for this medication."}</span><span>{lotNumbers.length > 0 ? " The lots below are ones this medication was dosed from before." : ""}</span><span> Set up inventory in </span></span><ProgressBarLink href="/settings/medications" className="text-link underline hover:text-link-hover">Settings → Medications</ProgressBarLink><span>.</span></div>;
    $[56] = hasShipments;
    $[57] = inventoryMode;
    $[58] = isWidened;
    $[59] = loading;
    $[60] = locationName;
    $[61] = lotNumbers;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== hasShipments || $[64] !== isWidened || $[65] !== loading || $[66] !== lotNumbers || $[67] !== productName) {
    t32 = !isWidened && !hasShipments && !loading && lotNumbers.map(entry_2 => <SearchSelectItem key={entry_2.lotNumber} value={entry_2.lotNumber} description={getLotHistoryDescription(entry_2)}>{lotRowLabel({
        lotNumber: entry_2.lotNumber,
        productName: entry_2.medicationName ?? productName
      })}</SearchSelectItem>);
    $[63] = hasShipments;
    $[64] = isWidened;
    $[65] = loading;
    $[66] = lotNumbers;
    $[67] = productName;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== getShipmentDescription || $[70] !== isWidened || $[71] !== widenedLots) {
    t33 = isWidened && widenedLots.map(lot_0 => <SearchSelectItem key={lot_0.id} value={lot_0.lotNumber ?? ""} description={getShipmentDescription(lot_0)}>{lotRowLabel({
        lotNumber: lot_0.lotNumber,
        productName: lot_0.medicationName
      })}</SearchSelectItem>);
    $[69] = getShipmentDescription;
    $[70] = isWidened;
    $[71] = widenedLots;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  const t34 = fieldValue?.type === "lot" ? fieldValue.value : "";
  let t35;
  if ($[73] !== lotNumbers || $[74] !== onValueChange || $[75] !== shipments) {
    t35 = value => {
      const typed = value.trim().toLowerCase();
      const matches_0 = shipments?.filter(s_2 => s_2.lotNumber?.trim().toLowerCase() === typed) ?? [];
      if (matches_0.length > 1) {
        setSelectionNotice("Multiple inventory entries have this lot number. Use the supplier, location, and expiry to select the correct lot above.");
        return;
      }
      const shipment_5 = matches_0[0];
      if (shipment_5) {
        onValueChange({
          type: "shipment",
          id: shipment_5.id,
          lotNumber: shipment_5.lotNumber || ""
        });
        return;
      }
      const existing = lotNumbers.find(entry_3 => entry_3.lotNumber.trim().toLowerCase() === typed);
      if (existing) {
        onValueChange({
          type: "lot",
          value: existing.lotNumber
        });
        return;
      }
      onValueChange({
        type: "lot",
        value
      });
    };
    $[73] = lotNumbers;
    $[74] = onValueChange;
    $[75] = shipments;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t34 || $[78] !== t35) {
    t36 = <DynamicSelectItem label="Add new lot number" addPlaceholder="Enter new lot number..." initialValue={t34} onAddOption={t35} />;
    $[77] = t34;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== selectionNotice) {
    t37 = selectionNotice && <p className="px-3 py-2 text-caption text-status-warning-text">{selectionNotice}</p>;
    $[80] = selectionNotice;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  const t38 = lotCategories ?? EMPTY_CATEGORIES;
  let t39;
  let t40;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => setIsWidened(true);
    t40 = () => {
      setIsWidened(false);
      setWidenCategory(null);
    };
    $[82] = t39;
    $[83] = t40;
  } else {
    t39 = $[82];
    t40 = $[83];
  }
  let t41;
  if ($[84] !== isWidened || $[85] !== productName || $[86] !== scopedListIsEmpty || $[87] !== t38 || $[88] !== widenCategory) {
    t41 = <WidenFooter isWidened={isWidened} scopedListIsEmpty={scopedListIsEmpty} productName={productName} categories={t38} category={widenCategory} onCategoryChange={setWidenCategory} onWiden={t39} onNarrow={t40} />;
    $[84] = isWidened;
    $[85] = productName;
    $[86] = scopedListIsEmpty;
    $[87] = t38;
    $[88] = widenCategory;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  let t42;
  if ($[90] !== t28 || $[91] !== t29 || $[92] !== t30 || $[93] !== t31 || $[94] !== t32 || $[95] !== t33 || $[96] !== t36 || $[97] !== t37 || $[98] !== t41) {
    t42 = <SearchSelectContent className="w-[var(--radix-popover-trigger-width)]" searchPlaceholder="Search lots or products..." emptyMessage={t28}>{t29}{t30}{t31}{t32}{t33}{t36}{t37}{t41}</SearchSelectContent>;
    $[90] = t28;
    $[91] = t29;
    $[92] = t30;
    $[93] = t31;
    $[94] = t32;
    $[95] = t33;
    $[96] = t36;
    $[97] = t37;
    $[98] = t41;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== disabled || $[101] !== handleValueChange || $[102] !== selectValue || $[103] !== t27 || $[104] !== t42) {
    t43 = <SearchSelect value={selectValue} onValueChange={handleValueChange} disabled={disabled}>{t27}{t42}</SearchSelect>;
    $[100] = disabled;
    $[101] = handleValueChange;
    $[102] = selectValue;
    $[103] = t27;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== fieldValue || $[107] !== handleClear) {
    t44 = fieldValue !== null && <button type="button" aria-label="Clear lot number" className="absolute right-8 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-foreground-secondary hover:bg-hover hover:text-foreground" onClick={handleClear}><RiCloseLine className="size-4" /></button>;
    $[106] = fieldValue;
    $[107] = handleClear;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== t24 || $[110] !== t43 || $[111] !== t44) {
    t45 = <div className={t24}>{t43}{t44}</div>;
    $[109] = t24;
    $[110] = t43;
    $[111] = t44;
    $[112] = t45;
  } else {
    t45 = $[112];
  }
  return t45;
}
function _temp4(entry_1) {
  return entry_1.medicationName;
}
function _temp3(shipment_1) {
  return shipment_1.medicationName;
}
function _temp2(lots) {
  return lots ?? EMPTY_SHIPMENT_LOTS;
}
function _temp(entry) {
  return [entry.lotNumber, entry] as const;
}
type LotRowLabelProps = {
  lotNumber?: string | null;
  productName?: string | null;
  chargedToday?: boolean;
  wrap?: boolean;
};

/**
 * One lot row's label: the code, and the product it belongs to.
 *
 * The product name is not decoration. Staff run lot codes that differ by a single
 * character, and a row that shows only the code cannot be checked against the vial in
 * hand. It renders on every row in every mode — a name that appears in one list and not
 * the next teaches staff not to rely on it.
 *
 * SearchSelect reads child text before it renders a custom component, so the lot
 * picker calls this directly. Everywhere else uses `<LotRowLabel />`.
 */
function lotRowLabel({
  lotNumber,
  productName,
  chargedToday = false,
  wrap = false
}: LotRowLabelProps) {
  return <span className={cx("flex min-w-0 items-center gap-1.5", wrap && "w-full flex-wrap")}>
      <span className={cx("font-medium", wrap ? "min-w-0 max-w-full break-words" : "shrink-0")}>
        {lotNumber || "No lot number"}
      </span>
      {productName && <span className={cx("min-w-0 text-foreground-secondary", wrap ? "max-w-full whitespace-normal break-words" : "truncate")}>
          {productName}
        </span>}
      {chargedToday && <Badge variant="success" size="xs" icon={RiReceiptLine} className="shrink-0">
          Charged today
        </Badge>}
    </span>;
}
export function LotRowLabel(props: LotRowLabelProps) {
  return lotRowLabel(props);
}

/** The explicit way in and out of the catalog-wide list, pinned below the options. */
function WidenFooter(t0) {
  const $ = _c(28);
  const {
    isWidened,
    scopedListIsEmpty,
    productName,
    categories,
    category,
    onCategoryChange,
    onWiden,
    onNarrow
  } = t0;
  if (!isWidened) {
    let t1;
    if ($[0] !== productName || $[1] !== scopedListIsEmpty) {
      t1 = scopedListIsEmpty && <p className="mb-1 text-caption text-foreground-secondary"><span>{`No lots for ${productName || "the selected medication"}.`}</span></p>;
      $[0] = productName;
      $[1] = scopedListIsEmpty;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== onWiden) {
      t2 = <button type="button" className="text-caption text-link underline hover:text-link-hover" onClick={onWiden}>Show lots from other products</button>;
      $[3] = onWiden;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t1 || $[6] !== t2) {
      t3 = <div className="border-t border-border px-3 py-2">{t1}{t2}</div>;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="shrink-0 text-caption text-foreground-secondary">Category</span>;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const t2 = category ?? "";
  let t3;
  if ($[9] !== onCategoryChange) {
    t3 = event => onCategoryChange(event.target.value || null);
    $[9] = onCategoryChange;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <option value="">All categories</option>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== categories) {
    t5 = categories.map(_temp5);
    $[12] = categories;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t5) {
    t6 = <div className="flex items-center gap-2">{t1}<select className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-caption text-foreground" value={t2} onChange={t3}>{t4}{t5}</select></div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-caption text-foreground-secondary">Lots from other products are recorded as a lot number only — stock is not deducted.</p>;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <RiArrowGoBackLine className="size-3.5" />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = productName ? `Back to ${productName} only` : "Back to this medication only";
  let t10;
  if ($[20] !== t9) {
    t10 = <span>{t9}</span>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onNarrow || $[23] !== t10) {
    t11 = <button type="button" className="flex items-center gap-1 text-caption text-link underline hover:text-link-hover" onClick={onNarrow}>{t8}{t10}</button>;
    $[22] = onNarrow;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t6) {
    t12 = <div className="space-y-2 border-t border-border px-3 py-2">{t6}{t7}{t11}</div>;
    $[25] = t11;
    $[26] = t6;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp5(name) {
  return <option key={name} value={name}>{name}</option>;
}